import "../Overview/EndpointOverview.css";
import "../Overview/EndpointDataSections.css";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import OverviewEmptyState from "../Overview/OverviewEmptyState";
import EndpointActionButton from "../Endpoints/EndpointActionButton";
import CardPagination from "../CardPagination";
import { useState, useMemo, useDeferredValue, useEffect } from "react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Label } from "../../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Plus,
  Search,
  Key,
  User,
  Calendar,
  Lock,
  Trash2,
  Eye,
  EyeOff,
  RotateCcw,
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import PublishComponent from "../PublishComponent";
import { Device } from "../interfaces/EndpointInterfaces";
import { Role } from "../interfaces/CommonInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import ConfirmationDialog from "../Endpoints/ConfirmationDialog";
import { getRolesList } from "../Endpoints/Api/EndpointApi";
import {
  createNewCredentials,
  deleteCredential,
  getCredentialsList,
  GatewayCredentialAuthType,
  GatewayCredentialProtocol,
  getUsersList,
  publishCredentials,
  revealCredentialPassword,
  rotateCredentialNow,
  storeGatewayCredential,
  updateCredentialAutoRotation,
} from "./Api/credentialapi";
import { CredentialInterface } from "./interfaces/credentialinterface";
import AddCredentialDialog from "./AddCredentialDialog";
import { Switch } from "../../ui/switch";
import useUserStore from "../../../services/userStore";
import { Skeleton } from "../../ui/skeleton";
import { formatTimeAgo } from "../formatTimeAgo";

interface CredentialsTabProps {
  device: Device;
}

type ManualDeviceCredentialAuth = GatewayCredentialAuthType;
type ManualDeviceCredentialProtocol = GatewayCredentialProtocol;

interface ManualDeviceCredentialFormState {
  label: string;
  username: string;
  protocol: ManualDeviceCredentialProtocol;
  authType: ManualDeviceCredentialAuth;
  privateKey: string;
  passphrase: string;
  password: string;
  confirmPassword: string;
}

type PendingConfirmationAction =
  | {
    type: "auto-rotate-toggle";
    credential: CredentialInterface;
    enabled: boolean;
  }
  | {
    type: "rotate-now";
    credential: CredentialInterface;
  }
  | {
    type: "delete-credential";
    credential: CredentialInterface;
  };

const initialManualDeviceCredentialForm: ManualDeviceCredentialFormState = {
  label: "",
  username: "",
  protocol: "SSH",
  authType: "Key",
  privateKey: "",
  passphrase: "",
  password: "",
  confirmPassword: "",
};

const getDeviceOs = (device: Device) => {
  const normalizedOs = (device.os || device.OperatingSystem || "").toLowerCase();
  return normalizedOs.includes("windows") ? "WINDOWS" : "LINUX";
};

const normalizeDeviceProtocol = (
  value?: string | null
): ManualDeviceCredentialProtocol | null => {
  const normalizedValue = `${value ?? ""}`.trim().toUpperCase();

  if (normalizedValue === "SSH") {
    return "SSH";
  }

  if (normalizedValue === "RDP") {
    return "RDP";
  }

  return null;
};

const getInitialManualDeviceCredentialForm = (
  protocol: ManualDeviceCredentialProtocol
): ManualDeviceCredentialFormState => ({
  ...initialManualDeviceCredentialForm,
  protocol,
  authType: protocol === "RDP" ? "Password" : "Key",
});

export default function CredentialDetails({ device }: CredentialsTabProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
  const [roles, setRoles] = useState<Role[]>([]);
  const { toast } = useToast();
  const [credentials, setCredentials] = useState<CredentialInterface[]>([]);
  const [users, setUsers] = useState<any[]>();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showManualAddDialog, setShowManualAddDialog] = useState(false);
  const [pendingConfirmationAction, setPendingConfirmationAction] =
    useState<PendingConfirmationAction | null>(null);
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const [autoRotateUpdatingIds, setAutoRotateUpdatingIds] = useState<Record<number, boolean>>({});
  const [deletingCredentialIds, setDeletingCredentialIds] = useState<Record<number, boolean>>({});
  const [rotatingNowIds, setRotatingNowIds] = useState<Record<number, boolean>>({});
  const [revealedPasswords, setRevealedPasswords] = useState<Record<number, string>>({});
  const [revealingPasswordIds, setRevealingPasswordIds] = useState<Record<number, boolean>>({});
  const [manualFormState, setManualFormState] = useState<ManualDeviceCredentialFormState>(
    initialManualDeviceCredentialForm
  );
  const itemsPerPage = 10;
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const { userId, email } = useUserStore((state) => state.user);
  const canAddCredential = !device?.isManuallyAddedMachine;
  const isManualDevice = Boolean(device?.isManuallyAddedMachine);
  const constrainedDeviceProtocol = useMemo(
    () => normalizeDeviceProtocol(device?.protocol),
    [device?.protocol]
  );
  const availableManualProtocols = useMemo<ManualDeviceCredentialProtocol[]>(
    () => (constrainedDeviceProtocol ? [constrainedDeviceProtocol] : ["SSH", "RDP"]),
    [constrainedDeviceProtocol]
  );
  const defaultManualProtocol = availableManualProtocols[0] || "SSH";

  // Loading states
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [, setLoadingRoles] = useState(true);
  const [, setLoadingUsers] = useState(true);

  const isManualPasswordMode =
    manualFormState.protocol === "RDP" || manualFormState.authType === "Password";

  const isManualFormValid = useMemo(() => {
    if (!manualFormState.label.trim() || !manualFormState.username.trim()) {
      return false;
    }

    if (isManualPasswordMode) {
      return (
        manualFormState.password.trim().length > 0 &&
        manualFormState.confirmPassword.trim().length > 0 &&
        manualFormState.password === manualFormState.confirmPassword
      );
    }

    return manualFormState.privateKey.trim().length > 0;
  }, [isManualPasswordMode, manualFormState]);

  const getRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await getRolesList(tenantName);
      if (response && response.length > 0) {
        const formattedRoles: Role[] = response
          .map((role: { id: number; roleName: string }) => ({
            id: String(role.id),
            name: role.roleName,
          }))
          .filter(
            (role: Role) =>
              role.name !== "epam-user" &&
              role.name !== "super-user" &&
              role.name !== "unassigned-user"
          );

        setRoles(formattedRoles);
      } else {
        setRoles([]);
      }
    } finally {
      setLoadingRoles(false);
    }
  };

  const getUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getUsersList(tenantName, device.id);
      if (response) {
        setUsers(response?.content);
      } else {
        setUsers([]);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const formatCreatedAt = (value?: string) => {
    if (!value) {
      return "N/A";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString();
  };

  const getCredentials = async (load = true) => {
    if (load) {
      setLoadingCredentials(true);
    }
    try {
      const response = await getCredentialsList(tenantName, device.id);
      setCredentials(response as CredentialInterface[]);
    } finally {
      setLoadingCredentials(false);
    }
  };

  // Reset current page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearchTerm]);

  useEffect(() => {
    if (isManualDevice) {
      getRoles();
      setUsers([]);
      setLoadingUsers(false);
      return;
    }

    getRoles();
    getUsers();
  }, [tenantName, device.id, isManualDevice]);

  // Memoized filtered credentials
  const filteredCredentials = useMemo(() => {
    if (!deferredSearchTerm.trim()) {
      return credentials;
    }
    const query = deferredSearchTerm.toLowerCase();
    return credentials.filter((credential) =>
      [
        credential.credentialLabel,
        credential.credentialUsername,
        credential.protocol,
        credential.protocolType,
        credential.protocolAuth,
        credential.operationSystem,
        credential.createdBy,
        credential.tenantName,
        credential.isfromGateway ? "gateway" : "manual",
      ].some((value) => `${value ?? ""}`.toLowerCase().includes(query))
    );
  }, [deferredSearchTerm, credentials]);

  // Pagination
  const totalPages = Math.ceil(filteredCredentials.length / itemsPerPage);
  const currentCredentials = filteredCredentials.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAddCredential = () => {
    if (!canAddCredential) return;
    setShowAddDialog(true);
  };

  const resetManualDeviceCredentialForm = () => {
    setManualFormState(getInitialManualDeviceCredentialForm(defaultManualProtocol));
  };

  useEffect(() => {
    setManualFormState((prev) => {
      if (availableManualProtocols.includes(prev.protocol)) {
        return prev;
      }

      return getInitialManualDeviceCredentialForm(defaultManualProtocol);
    });
  }, [availableManualProtocols, defaultManualProtocol]);

  const handleOpenManualCredentialDialog = () => {
    resetManualDeviceCredentialForm();
    setShowManualAddDialog(true);
  };

  const handleManualProtocolChange = (value: string) => {
    const nextProtocol: ManualDeviceCredentialProtocol =
      value === "RDP" ? "RDP" : "SSH";

    setManualFormState((prev) => {
      if (nextProtocol === "RDP") {
        return {
          ...prev,
          protocol: "RDP",
          authType: "Password",
          privateKey: "",
          passphrase: "",
        };
      }

      return {
        ...prev,
        protocol: "SSH",
        authType: prev.authType === "Password" ? "Key" : prev.authType,
      };
    });
  };

  const handleManualAuthTypeChange = (value: string) => {
    const nextAuthType: ManualDeviceCredentialAuth =
      value === "Password" ? "Password" : "Key";

    setManualFormState((prev) =>
      nextAuthType === "Key"
        ? {
          ...prev,
          authType: "Key",
          password: "",
          confirmPassword: "",
        }
        : {
          ...prev,
          authType: "Password",
          privateKey: "",
          passphrase: "",
        }
    );
  };

  const handleStoreManualCredential = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (!tenantName.trim()) {
      toast({
        title: "Tenant missing",
        description: "Select a tenant before storing credentials.",
        variant: "destructive",
      });
      return;
    }

    if (!email || userId == null) {
      toast({
        title: "User context missing",
        description: "Unable to identify user details to store credentials.",
        variant: "destructive",
      });
      return;
    }

    // Check if username already exists for manual devices
    const usernameExists = credentials.some(
      (cred) => cred.credentialUsername.toLowerCase() === manualFormState.username.toLowerCase()
    );

    if (usernameExists) {
      toast({
        title: "Duplicate username",
        description: `Username "${manualFormState.username}" already exists for this device. Please use a different username.`,
        variant: "destructive",
      });
      return;
    }

    if (
      isManualPasswordMode &&
      manualFormState.password !== manualFormState.confirmPassword
    ) {
      toast({
        title: "Validation error",
        description: "Password and confirm password must match.",
        variant: "destructive",
      });
      return;
    }

    setIsManualSubmitting(true);
    const rawPrivateKey = manualFormState.privateKey;
    const normalizedPrivateKey = rawPrivateKey.includes("\\n")
      ? rawPrivateKey.replace(/\\n/g, "\n")
      : rawPrivateKey;
    const isSshKeyMode =
      manualFormState.protocol === "SSH" && manualFormState.authType === "Key";
    try {
      await storeGatewayCredential({
        label: manualFormState.label.trim(),
        username: manualFormState.username.trim(),
        password: isManualPasswordMode
          ? manualFormState.password
          : isSshKeyMode
            ? normalizedPrivateKey
            : "",
        retypePassword: isManualPasswordMode ? manualFormState.confirmPassword : "",
        tenantName,
        os: getDeviceOs(device),
        computerId: device.id,
        createdBy: email,
        userId: String(userId),
        fromGateway: true,
        protocol: manualFormState.protocol,
        protocolAuth:
          manualFormState.protocol === "RDP"
            ? "Password"
            : manualFormState.authType,
        privateKey: isSshKeyMode ? normalizedPrivateKey : "",
        passPharse: isSshKeyMode ? manualFormState.passphrase : "",
      });

      toast({
        title: "Credential stored",
        description: `Credential "${manualFormState.username}" has been stored for this device.`,
      });

      setShowManualAddDialog(false);
      resetManualDeviceCredentialForm();
      await getCredentials(false);
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Failed to store device credential.";

      toast({
        title: "Store failed",
        description,
        variant: "destructive",
      });
    } finally {
      setIsManualSubmitting(false);
    }
  };

  const handlePublishCredential = async (
    credentialId: any,
    credentialName: string,
    selectedRoles: string[]
  ) => {
    if (!tenantName) return;
    let response = await publishCredentials(tenantName, credentialId, selectedRoles);
    if (response) {
      toast({
        title: "Credential Published",
        description: selectedRoles.length > 0 ? `Credential "${credentialName}" published to ${selectedRoles.length} role(s).` : "Unpublished from all roles",
      });
      getCredentials(false);
    }


  };

  const handleDeleteCredential = async (
    credential: CredentialInterface
  ) => {
    const label = credential.credentialLabel;
    const credentialUsername = credential.credentialUsername;

    setDeletingCredentialIds((prev) => ({ ...prev, [credential.id]: true }));
    try {
      await deleteCredential({
        label,
        deviceId: device.id,
      });

      toast({
        title: "Credential Deleted",
        description: `Credential "${credentialUsername}" has been deleted.`,
        variant: "destructive",
      });
      getCredentials(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete credential "${credentialUsername}".`,
        variant: "destructive",
      });
    } finally {
      setDeletingCredentialIds((prev) => ({ ...prev, [credential.id]: false }));
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getCredentialLabel = (credential: CredentialInterface) =>
    (credential.credentialLabel || "").trim();

  const isCredentialAutoRotateEnabled = (credential: CredentialInterface) =>
    Boolean(
      credential.isAutoRotate ??
      credential.autoRotate ??
      credential.autoRotationEnabled ??
      credential.isAutoRotateEnabled
    );

  const getCredentialLastRotatedDate = (credential: CredentialInterface) =>
    credential.lastRotatedDate ?? null;

  const openAutoRotateConfirmation = (
    credential: CredentialInterface,
    enabled: boolean
  ) => {
    setPendingConfirmationAction({
      type: "auto-rotate-toggle",
      credential,
      enabled,
    });
  };

  const openRotateNowConfirmation = (credential: CredentialInterface) => {
    setPendingConfirmationAction({
      type: "rotate-now",
      credential,
    });
  };

  const openDeleteCredentialConfirmation = (credential: CredentialInterface) => {
    setPendingConfirmationAction({
      type: "delete-credential",
      credential,
    });
  };

  const closeConfirmationDialog = () => {
    setPendingConfirmationAction(null);
  };

  const handleAutoRotateToggle = async (
    credential: CredentialInterface,
    enabled: boolean
  ) => {
    const label = getCredentialLabel(credential);

    if (!label) {
      toast({
        title: "Auto-rotation update failed",
        description: "Credential label is missing.",
        variant: "destructive",
      });
      return;
    }

    setAutoRotateUpdatingIds((prev) => ({ ...prev, [credential.id]: true }));
    try {
      await updateCredentialAutoRotation({
        computerId: device.id,
        label,
        enabled,
      });

      toast({
        title: "Auto-rotation updated",
        description: `Auto-rotation ${enabled ? "enabled" : "disabled"} for "${credential.credentialUsername}".`,
      });

      await getCredentials(false);
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Failed to update auto-rotation.";

      toast({
        title: "Auto-rotation update failed",
        description,
        variant: "destructive",
      });
    } finally {
      setAutoRotateUpdatingIds((prev) => ({ ...prev, [credential.id]: false }));
    }
  };

  const handleToggleRevealPassword = async (credential: CredentialInterface) => {
    const { id } = credential;

    // If already revealed, hide it
    if (revealedPasswords[id] !== undefined) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }

    const label = getCredentialLabel(credential);
    if (!label) {
      toast({
        title: "Reveal failed",
        description: "Credential label is missing.",
        variant: "destructive",
      });
      return;
    }

    setRevealingPasswordIds((prev) => ({ ...prev, [id]: true }));
    try {
      const password = await revealCredentialPassword({
        computerId: device.id,
        tenantName,
        label,
      });
      setRevealedPasswords((prev) => ({ ...prev, [id]: password }));
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Failed to reveal credential password.";
      toast({
        title: "Reveal failed",
        description,
        variant: "destructive",
      });
    } finally {
      setRevealingPasswordIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleRotateNow = async (credential: CredentialInterface) => {
    const label = getCredentialLabel(credential);

    if (!label) {
      toast({
        title: "Rotate failed",
        description: "Credential label is missing.",
        variant: "destructive",
      });
      return;
    }

    setRotatingNowIds((prev) => ({ ...prev, [credential.id]: true }));
    try {
      await rotateCredentialNow({
        computerId: device.id,
        label,
        username: credential.credentialUsername || "",
      });

      toast({
        title: "Rotation triggered",
        description: `Password rotation requested for "${credential.credentialUsername}".`,
      });
    } catch (error: any) {
      const description =
        typeof error === "string"
          ? error
          : error?.message || "Failed to rotate credential.";

      toast({
        title: "Rotate failed",
        description,
        variant: "destructive",
      });
    } finally {
      setRotatingNowIds((prev) => ({ ...prev, [credential.id]: false }));
    }
  };

  const handleConfirmPendingAction = async () => {
    if (!pendingConfirmationAction) {
      return;
    }

    if (pendingConfirmationAction.type === "auto-rotate-toggle") {
      await handleAutoRotateToggle(
        pendingConfirmationAction.credential,
        pendingConfirmationAction.enabled
      );
      return;
    }

    if (pendingConfirmationAction.type === "delete-credential") {
      await handleDeleteCredential(pendingConfirmationAction.credential);
      return;
    }

    await handleRotateNow(pendingConfirmationAction.credential);
  };

  const getConfirmationDialogContent = () => {
    if (!pendingConfirmationAction) {
      return null;
    }

    const credentialName =
      pendingConfirmationAction.credential.credentialUsername ||
      getCredentialLabel(pendingConfirmationAction.credential) ||
      "this credential";

    if (pendingConfirmationAction.type === "auto-rotate-toggle") {
      return pendingConfirmationAction.enabled
        ? {
          title: "Enable Auto-Rotation",
          description:
            `This option will rotate the windows user password after each session for user "${credentialName}".`,
          confirmText: "Continue",
          variant: "warning" as const,
          loading: Boolean(
            autoRotateUpdatingIds[pendingConfirmationAction.credential.id]
          ),
        }
        : {
          title: "Disable Auto-Rotation",
          description: `Are you sure you want to disable auto-rotation for user "${credentialName}"?`,
          confirmText: "Continue",
          variant: "warning" as const,
          loading: Boolean(
            autoRotateUpdatingIds[pendingConfirmationAction.credential.id]
          ),
        };
    }

    if (pendingConfirmationAction.type === "delete-credential") {
      const lastRotatedDate = getCredentialLastRotatedDate(
        pendingConfirmationAction.credential
      );

      return {
        title: "Delete Credential",
        description: lastRotatedDate
          ? `Are you sure want to delete the credential for user ${credentialName}, Make sure to copy the credential as it is rotated ${formatTimeAgo(lastRotatedDate)}`
          : `Are you sure want to delete the credential for user ${credentialName}`,
        confirmText: "Continue",
        variant: "destructive" as const,
        loading: Boolean(
          deletingCredentialIds[pendingConfirmationAction.credential.id]
        ),
      };
    }

    return {
      title: "Rotate Credential",
      description: `This will rotate the windows user password for user "${credentialName}". Are you sure you want to continue?`,
      confirmText: "Continue",
      variant: "warning" as const,
      loading: Boolean(rotatingNowIds[pendingConfirmationAction.credential.id]),
    };
  };

  const handleAddCredentialSubmit = async (newCredential: {
    label: string;
    username: string;
    password: string;
    autoRotate: boolean;
  }) => {
    if (userId == null) return;
    await createNewCredentials({
      label: newCredential.label,
      username: newCredential.username,
      password: newCredential.password,
      autoRotate: newCredential.autoRotate,
      userId,
      tenantName,
      email,
      deviceId: device.id,
    });
    getCredentials(false);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      getCredentials(false);
    }, 5000)
    getCredentials();
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="endpoint-overview endpoint-data-sections endpoint-credentials">
      {/* Header Controls */}
      <Card className="overview-card endpoint-section-toolbar">
        <div className="endpoint-toolbar-fields flex items-center justify-between">
          <div className="endpoint-filter-field endpoint-filter-search flex items-center gap-4">
            <span className="endpoint-filter-label"><Search />Search</span>
            <div className="flex items-center max-w-md gap-2">
              <div className="relative flex-1">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder={isManualDevice ? "Search by label, username, protocol, auth type..." : "Search credentials..."}
                  aria-label="Search credentials"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-80"
                  data-testid="input-search"
                />
              </div>
            </div>
          </div>

          {isManualDevice ? (
            <EndpointActionButton
              onClick={handleOpenManualCredentialDialog}
              data-testid="button-add-device-credential"
            >
              <Plus className="w-4 h-4" />
              Add Device credential
            </EndpointActionButton>
          ) : (
            canAddCredential && (
              <EndpointActionButton
                onClick={handleAddCredential}
                data-testid="button-add-credential"
              >
                <Plus className="w-4 h-4" />
                Add New Credential
              </EndpointActionButton>
            )
          )}
        </div>
      </Card>

      {/* Credentials Table */}
      <div className="endpoint-section-table">
        <Card className="overview-card endpoint-section-table-card performance-card">
          <OverviewCardHeader
            fromdetails
            title="Credentials"
            description="Account credentials and access for this endpoint"
            icon={Key}
            detail={!loadingCredentials ? `${filteredCredentials.length.toLocaleString()} credentials` : undefined}
          />
          <CardContent className="overview-card-content endpoint-table-content">
            <div>
              <Table className="endpoint-data-table performance-table">
                <TableHeader>
                  {isManualDevice ? (
                    <TableRow className="bg-muted/60 hover:bg-muted/60">
                      <TableHead className="font-semibold text-foreground">Credential</TableHead>
                      <TableHead className="font-semibold text-foreground">Protocol</TableHead>
                      <TableHead className="font-semibold text-foreground">Auth Type</TableHead>
                      <TableHead className="font-semibold text-foreground">OS</TableHead>
                      <TableHead className="font-semibold text-foreground">Created</TableHead>
                      <TableHead className="font-semibold text-center text-foreground">Publish</TableHead>
                      <TableHead className="font-semibold text-center text-foreground">Actions</TableHead>
                    </TableRow>
                  ) : (
                    <TableRow className="bg-slate-800 hover:bg-slate-800">
                      <TableHead className="font-medium text-white w-44">
                        <div className="flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          Name
                        </div>
                      </TableHead>
                      <TableHead className="w-32 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Created At
                        </div>
                      </TableHead>
                      <TableHead className="w-48 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Created By
                        </div>
                      </TableHead>
                      <TableHead className="w-24 font-medium text-center text-white">
                        Auto-Rotate
                      </TableHead>
                      <TableHead className="w-48 font-medium text-white">
                        Password
                      </TableHead>
                      <TableHead className="font-medium text-center text-white w-28">
                        Publish
                      </TableHead>
                      <TableHead className="w-24 font-medium text-center text-white">
                        Actions
                      </TableHead>
                    </TableRow>
                  )}
                </TableHeader>
                <TableBody>
                  {loadingCredentials ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        <TableCell colSpan={isManualDevice ? 7 : 7} className="py-4">
                          <Skeleton className="w-full h-6" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : currentCredentials.length > 0 ? (
                    currentCredentials.map((credential) => (
                      isManualDevice ? (
                        <TableRow
                          key={credential.id}
                          data-testid={`credential-${credential.id}`}
                          className="hover:bg-muted/40"
                        >
                          <TableCell>
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-foreground">
                                {credential.credentialUsername || "N/A"}

                              </div>
                              <div className="text-xs text-muted-foreground">
                                Label: {credential.rawLabel || credential.credentialLabel || "N/A"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {(credential.protocol || "N/A").toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {(
                                credential.protocolType ||
                                credential.protocolAuth ||
                                "N/A"
                              ).toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {(credential.operationSystem || "N/A").toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{formatCreatedAt(credential.createdDate)}</div>

                          </TableCell>

                          <TableCell
                            className="text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PublishComponent
                              entityName={credential.credentialLabel || credential.credentialUsername}
                              entityType="credential"
                              availableRoles={roles}
                              publishedRoles={credential.publishedroleIds || []}
                              onPublish={(selectedRoles) =>
                                handlePublishCredential(
                                  credential.id,
                                  credential.credentialLabel || credential.credentialUsername,
                                  selectedRoles
                                )
                              }
                              buttonVariant="outline"
                            />
                          </TableCell>

                          <TableCell className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteCredentialConfirmation(credential)}
                              className="w-8 h-8 p-0 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                              disabled={Boolean(deletingCredentialIds[credential.id])}
                              data-testid={`button-delete-${credential.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={credential.id}
                          data-testid={`credential-${credential.id}`}
                          className="hover:bg-muted/50"
                        >
                          {(() => {
                            const isAutoRotateEnabled =
                              isCredentialAutoRotateEnabled(credential);
                            const lastRotatedDate =
                              getCredentialLastRotatedDate(credential);

                            return (
                              <>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`endpoint-row-icon w-8 h-8 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/20`}
                                    >
                                      <Key className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium">
                                        {credential.credentialUsername}
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                  {credential.createdDate.split("T")[0]}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {credential.createdBy}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Switch
                                    checked={isAutoRotateEnabled}
                                    onCheckedChange={(checked) =>
                                      openAutoRotateConfirmation(credential, checked)
                                    }
                                    className="scale-75"
                                    disabled={Boolean(autoRotateUpdatingIds[credential.id])}
                                    data-testid={`switch-auto-rotate-${credential.id}`}
                                  />
                                </TableCell>
                                <TableCell className="py-5">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-1.5">
                                      <code className="flex-1 px-3 py-1 font-mono text-xs rounded bg-slate-100 dark:bg-slate-800">
                                        {revealedPasswords[credential.id] !== undefined
                                          ? revealedPasswords[credential.id]
                                          : "••••••••••"}
                                      </code>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="flex-shrink-0 w-6 h-6 p-0"
                                        data-testid={`button-toggle-password-${credential.id}`}
                                        disabled={Boolean(revealingPasswordIds[credential.id]) || !lastRotatedDate}
                                        onClick={() => handleToggleRevealPassword(credential)}
                                      >
                                        {revealedPasswords[credential.id] !== undefined ? (
                                          <Eye className="w-3 h-3" />
                                        ) : (
                                          <EyeOff className="w-3 h-3" />
                                        )}
                                      </Button>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-5 text-xs px-1.5 py-0 text-blue-600 border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950 w-full"
                                      data-testid={`button-rotate-now-${credential.id}`}
                                      onClick={() => openRotateNowConfirmation(credential)}
                                      disabled={
                                        Boolean(rotatingNowIds[credential.id]) ||
                                        !isAutoRotateEnabled
                                      }
                                    >
                                      <RotateCcw className="w-2.5 h-2.5 mr-1" />
                                      {rotatingNowIds[credential.id] ? "Rotating..." : "Rotate"}
                                    </Button>
                                    {lastRotatedDate ? (
                                      <span>
                                        Last Rotated: {formatTimeAgo(lastRotatedDate)}
                                      </span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell
                                  className="text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <PublishComponent
                                    entityName={credential.credentialUsername}
                                    entityType="credential"
                                    availableRoles={roles}
                                    publishedRoles={credential.publishedroleIds || []}
                                    onPublish={(selectedRoles) =>
                                      handlePublishCredential(
                                        credential.id,
                                        credential.credentialUsername,
                                        selectedRoles
                                      )
                                    }
                                    buttonVariant="outline"
                                  />
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openDeleteCredentialConfirmation(credential)}
                                    className="w-8 h-8 p-0 text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                                    disabled={Boolean(deletingCredentialIds[credential.id])}
                                    data-testid={`button-delete-${credential.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </>
                            );
                          })()}
                        </TableRow>
                      )
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-8 text-center text-muted-foreground"
                      >
                        <OverviewEmptyState icon={Key} message="No credentials found" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          {/* Pagination */}
          {!loadingCredentials && totalPages > 1 && (
            <div className="endpoint-table-pagination endpoint-pagination-bar border-t">
              <span className="endpoint-pagination-summary">
                Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredCredentials.length)} of {filteredCredentials.length.toLocaleString()} credentials
              </span>
              <CardPagination
                label="Credential pages"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </Card>
      </div>

      <Dialog
        open={showManualAddDialog}
        onOpenChange={(open) => {
          setShowManualAddDialog(open);
          if (!open && !isManualSubmitting) {
            resetManualDeviceCredentialForm();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="dialog-add-device-credential"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Add Device Credential
            </DialogTitle>
            <DialogDescription>
              Configure protocol and authentication details for this manual device credential.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleStoreManualCredential} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="manual-device-credential-label">Credential Label</Label>
                <Input
                  id="manual-device-credential-label"
                  value={manualFormState.label}
                  onChange={(event) =>
                    setManualFormState((prev) => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  placeholder="e.g., manual-device-admin"
                  data-testid="input-manual-device-credential-label"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual-device-credential-protocol">Protocol</Label>
                <Select
                  value={manualFormState.protocol}
                  onValueChange={handleManualProtocolChange}
                >
                  <SelectTrigger
                    id="manual-device-credential-protocol"
                    data-testid="select-manual-device-credential-protocol"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableManualProtocols.map((protocolOption) => (
                      <SelectItem key={protocolOption} value={protocolOption}>
                        {protocolOption}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {manualFormState.protocol === "SSH" && (
              <div className="space-y-2">
                <Label>Authentication Type</Label>
                <RadioGroup
                  value={manualFormState.authType}
                  onValueChange={handleManualAuthTypeChange}
                  className="grid grid-cols-2 gap-4"
                  data-testid="radio-manual-device-auth-type"
                >
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <RadioGroupItem value="Key" id="manual-device-auth-key" />
                    <Label htmlFor="manual-device-auth-key" className="cursor-pointer">
                      SSH Key
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <RadioGroupItem
                      value="Password"
                      id="manual-device-auth-password"
                    />
                    <Label
                      htmlFor="manual-device-auth-password"
                      className="cursor-pointer"
                    >
                      Password
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="manual-device-credential-username">Username</Label>
              <Input
                id="manual-device-credential-username"
                value={manualFormState.username}
                onChange={(event) =>
                  setManualFormState((prev) => ({
                    ...prev,
                    username: event.target.value,
                  }))
                }
                placeholder="Enter username"
                data-testid="input-manual-device-credential-username"
              />
            </div>

            {isManualPasswordMode ? (
              <>
                <div className="space-y-2">
                  <Label
                    htmlFor="manual-device-credential-password"
                    className="flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <Input
                    id="manual-device-credential-password"
                    type="password"
                    value={manualFormState.password}
                    onChange={(event) =>
                      setManualFormState((prev) => ({
                        ...prev,
                        password: event.target.value,
                      }))
                    }
                    placeholder="Enter password"
                    data-testid="input-manual-device-credential-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="manual-device-credential-confirm-password"
                    className="flex items-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Confirm Password
                  </Label>
                  <Input
                    id="manual-device-credential-confirm-password"
                    type="password"
                    value={manualFormState.confirmPassword}
                    onChange={(event) =>
                      setManualFormState((prev) => ({
                        ...prev,
                        confirmPassword: event.target.value,
                      }))
                    }
                    placeholder="Confirm password"
                    data-testid="input-manual-device-credential-confirm-password"
                  />
                  {manualFormState.password &&
                    manualFormState.confirmPassword &&
                    manualFormState.password !== manualFormState.confirmPassword && (
                      <p className="text-sm text-red-600">
                        Password and confirm password must match.
                      </p>
                    )}
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label
                    htmlFor="manual-device-credential-private-key"
                    className="flex items-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    Private Key
                  </Label>
                  <Textarea
                    id="manual-device-credential-private-key"
                    value={manualFormState.privateKey}
                    onChange={(event) =>
                      setManualFormState((prev) => ({
                        ...prev,
                        privateKey: event.target.value,
                      }))
                    }
                    placeholder="-----BEGIN PRIVATE KEY-----"
                    className="min-h-[120px] font-mono text-sm"
                    data-testid="textarea-manual-device-credential-private-key"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="manual-device-credential-passphrase"
                    className="flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    Passphrase (Optional)
                  </Label>
                  <Input
                    id="manual-device-credential-passphrase"
                    type="password"
                    value={manualFormState.passphrase}
                    onChange={(event) =>
                      setManualFormState((prev) => ({
                        ...prev,
                        passphrase: event.target.value,
                      }))
                    }
                    placeholder="Enter optional passphrase"
                    data-testid="input-manual-device-credential-passphrase"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetManualDeviceCredentialForm();
                  setShowManualAddDialog(false);
                }}
                disabled={isManualSubmitting}
                data-testid="button-cancel-manual-device-credential"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={!isManualFormValid || isManualSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="button-store-manual-device-credential"
              >
                {isManualSubmitting ? "Storing..." : "Store Credential"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AddCredentialDialog
        open={canAddCredential && showAddDialog}
        onOpenChange={(open) => {
          if (!canAddCredential) return;
          setShowAddDialog(open);
        }}
        onAdd={handleAddCredentialSubmit}
        users={users || []}
        existingCredentials={credentials}
        isManualDevice={isManualDevice}
      />

      {(() => {
        const dialogContent = getConfirmationDialogContent();

        if (!dialogContent) {
          return null;
        }

        return (
          <ConfirmationDialog
            open={Boolean(pendingConfirmationAction)}
            onOpenChange={(open) => {
              if (!open) {
                closeConfirmationDialog();
              }
            }}
            title={dialogContent.title}
            description={dialogContent.description}
            confirmText={dialogContent.confirmText}
            cancelText="Cancel"
            variant={dialogContent.variant}
            loading={dialogContent.loading}
            onConfirm={() => {
              void handleConfirmPendingAction();
            }}
            onCancel={closeConfirmationDialog}
          />
        );
      })()}
    </div>
  );
}
